import type { SupplierContactInput } from "@hisense/shared";
import type { FieldSpan } from "@/components/ui/FormLayout";

/*
 * Bố cục trang Sửa nhà cung cấp (19/09/2026, cùng chuẩn trang Sửa khách hàng).
 *
 * Production 19/09/2026 chỉ có 9 nhà cung cấp, mỗi nhà đúng một người liên hệ, 30 ngày qua không ai lập hay sửa — không đủ số liệu
 * để chọn cột như ở khách hàng, nên theo khuôn khách hàng và theo nghiệp vụ: điện thoại 1, email, chuyên môn là thứ người mua cần khi
 * gửi đơn; điện thoại 2 (1/9) và WeChat (0/9) là cột phụ — ẩn khi không ai dùng, tự hiện và không ẩn được khi có.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường của nhà cung cấp và của người liên hệ có đúng một chỗ.
 */

/** Một hàng người liên hệ đang sửa — mọi ô là chuỗi để ô nhập luôn được kiểm soát; thứ tự lấy theo vị trí khi lưu. */
export type SupplierContactForm = Required<Omit<SupplierContactInput, "sortOrder">>;

/** Phần công ty đang sửa. */
export interface SupplierFormDraft {
  companyName: string;
  /** Đội dùng nhà cung cấp này (29/09/2026): "" = chưa chọn (nhà cung cấp mới), "unclassified" = chưa phân loại. */
  classification: "" | "vietnam" | "china" | "unclassified";
  address: string;
  phone: string;
  email: string;
}

/** Ô trên lưới 12 cột, mỗi hàng kín 12 ô: tên công ty 6 + khu vực 3 + điện thoại 3; địa chỉ 8 + email 4. */
export const SUPPLIER_COMPANY_FIELDS: readonly { key: keyof SupplierFormDraft; span: FieldSpan }[] = [
  { key: "companyName", span: 6 }, { key: "classification", span: 3 }, { key: "phone", span: 3 },
  { key: "address", span: 8 }, { key: "email", span: 4 },
];

export const SUPPLIER_CONTACT_MAIN_COLUMNS = ["name", "phone1", "email", "specialization"] as const;
export const SUPPLIER_CONTACT_SIDE_COLUMNS = ["phone2", "wechat"] as const;
export type SupplierContactColumn = (typeof SUPPLIER_CONTACT_MAIN_COLUMNS)[number] | (typeof SUPPLIER_CONTACT_SIDE_COLUMNS)[number];

/** Độ rộng cố định (rem); `null` là cột co giãn (tên, email), tối thiểu 11rem mỗi cột. Chuyên môn là ô chọn theo danh sách cài đặt. */
export const SUPPLIER_CONTACT_WIDTH: Record<SupplierContactColumn, number | null> = {
  name: null, phone1: 9.5, email: null, specialization: 10, phone2: 9, wechat: 9,
};
export const SUPPLIER_CONTACT_FLEX_MIN_REM = 11;
/** Cột số thứ tự và cột nút xoá. */
export const SUPPLIER_CONTACT_EDGE_REM = 2 + 2;

/**
 * Cờ của người liên hệ không phải một cột: ô khoá "Chỉ quản trị viên xem" (0097, như liên hệ riêng tư của khách) nằm trong ô tên,
 * chỉ hiện với quản trị viên — bảng không rộng thêm.
 */
export const SUPPLIER_CONTACT_FLAGS = ["isPrivate"] as const;

export function blankSupplierContact(): SupplierContactForm {
  return { name: "", phone1: "", phone2: "", email: "", wechat: "", specialization: "", isPrivate: false };
}

/** Hàng người dùng chưa gõ gì — bị bỏ khi lưu. */
export const isBlankSupplierContact = (c: SupplierContactForm) =>
  [...SUPPLIER_CONTACT_MAIN_COLUMNS, ...SUPPLIER_CONTACT_SIDE_COLUMNS].every((k) => c[k].trim() === "");

/** Có người liên hệ nào dùng cột phụ không — có thì cột phụ hiện và không ẩn được. */
export const supplierContactSideUsed = (contacts: readonly SupplierContactForm[]) =>
  contacts.some((c) => SUPPLIER_CONTACT_SIDE_COLUMNS.some((k) => c[k].trim() !== ""));
